import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { RouteAction, RouteLocation, SyncJourneyLocally } from "config";
import Journey from "containers/Journey/Journey";
import JourneyProgress from "containers/JourneyProgress/JourneyProgress";
import { usePrompt, useTranslation } from "modules";
import { RootState, useAppDispatch } from "store";
import { finishJourney } from "store/actions/journey";
import { setModal } from "store/actions/modal";
import { deprecatedJourneys } from "store/mock/journey.mock";
import { buildJourneyExitContent } from "utils/journey";

import { find, get, includes, isEmpty, isUndefined } from "lodash-es";
import store2 from "store2";

import JourneySectionProvider from "./JourneySectionProvider";

const { local } = store2;

const JourneyLocalSync: FC<{ active?: boolean }> = props => {
  const { active = true } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Capture the path at mount time so POP handler navigates back to the
  // original path, not the current one (which changes during the journey).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentPath = useMemo(() => location.pathname, []);

  const journey = useSelector((state: RootState) => state.journey);
  const user = useSelector((state: RootState) => state.user);

  const anyJourneysActive = !isEmpty(journey.activeJourneys);
  const rootJourney = find(journey.collection, item => isUndefined(item?.parent));
  const rootJourneyId = get(rootJourney, "id", "");

  const setJourneyExitModal = (location: RouteLocation, action: RouteAction) => {
    if (action === "POP") {
      navigate(currentPath);
    } else {
      const content = buildJourneyExitContent(rootJourney, t);

      setTimeout(() => {
        dispatch(setModal("JourneyExit", { id: rootJourneyId, content, location }));
      }, 100);
    }
  };

  usePrompt(anyJourneysActive, setJourneyExitModal);

  useEffect(() => {
    if (anyJourneysActive) {
      window.onbeforeunload = (e: BeforeUnloadEvent) => {
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = "";
      };
    } else {
      window.onbeforeunload = null;
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, [anyJourneysActive]);

  useEffect(() => {
    if (SyncJourneyLocally && active && !isEmpty(journey.activeJourneys)) {
      const userId = user.id;
      const storage = isEmpty(userId) ? local : local.namespace(userId as string);
      storage.set("journeyProgress", journey);
    }
  }, [active, journey, user.id]);

  return null;
};

export const JourneyManager: FC = () => {
  const { collection } = useSelector((state: RootState) => state.journey);
  const rootJourney = find(collection, item => isUndefined(item?.parent));
  const rootJourneyId = get(rootJourney, "id", "");
  const rootJourneyMetadata = rootJourney?.metadata;
  const dispatch = useAppDispatch();

  // End journeys that are no longer supported with `finishJourney`
  useEffect(() => {
    const endDeprecatedJourneys = async () => {
      if (includes(deprecatedJourneys, rootJourneyId)) {
        return await dispatch(finishJourney({ id: rootJourneyId }));
      }
    };

    endDeprecatedJourneys();
  }, [dispatch, rootJourneyId]);

  if (isEmpty(rootJourneyId)) {
    return null;
  }

  return (
    <JourneySectionProvider>
      <JourneyLocalSync />
      <div className="journey-experience__container">
        <div className="journey-experience__wrapper">
          <JourneyProgress />
          <div className="journey-experience__content-wrapper">
            <Journey id={rootJourneyId} metadata={rootJourneyMetadata} />
          </div>
        </div>
      </div>
    </JourneySectionProvider>
  );
};
